// Venus on-chain fetch (Core Pool on BNB Chain) using ethers
import { providers, Contract, BigNumber as EBig } from 'ethers';
import BigNumber from 'bignumber.js';

const BNB_CHAIN_ID = 56;
const BNB_RPC_CANDIDATES = [
  'https://bsc-dataseed.binance.org',
  'https://rpc.ankr.com/bsc',
];

// Comptroller (Unitroller) address for Venus Core Pool (BSC mainnet)
const VENUS_COMPTROLLER = '0xfd36e2c2a6789db23113685031d7f16329158384';

// Minimal ABIs
const ComptrollerAbi = [
  { constant: true, inputs: [], name: 'getAllMarkets', outputs: [{ type: 'address[]' }], stateMutability: 'view', type: 'function' },
  { constant: true, inputs: [], name: 'oracle', outputs: [{ type: 'address' }], stateMutability: 'view', type: 'function' },
  { constant: true, inputs: [{ type: 'address', name: 'account' }], name: 'getAssetsIn', outputs: [{ type: 'address[]' }], stateMutability: 'view', type: 'function' },
  { constant: true, inputs: [{ type: 'address', name: '' }], name: 'markets', outputs: [{ type: 'bool' }, { type: 'uint256' }, { type: 'bool' }], stateMutability: 'view', type: 'function' },
];

const PriceOracleAbi = [
  { constant: true, inputs: [{ type: 'address', name: 'vToken' }], name: 'getUnderlyingPrice', outputs: [{ type: 'uint256' }], stateMutability: 'view', type: 'function' },
];

const VTokenAbi = [
  { constant: true, inputs: [{ type: 'address', name: 'owner' }], name: 'balanceOf', outputs: [{ type: 'uint256' }], stateMutability: 'view', type: 'function' },
  { constant: true, inputs: [{ type: 'address', name: 'owner' }], name: 'borrowBalanceStored', outputs: [{ type: 'uint256' }], stateMutability: 'view', type: 'function' },
  { constant: true, inputs: [], name: 'exchangeRateStored', outputs: [{ type: 'uint256' }], stateMutability: 'view', type: 'function' },
  { constant: true, inputs: [], name: 'symbol', outputs: [{ type: 'string' }], stateMutability: 'view', type: 'function' },
  { constant: true, inputs: [], name: 'name', outputs: [{ type: 'string' }], stateMutability: 'view', type: 'function' },
  { constant: true, inputs: [], name: 'decimals', outputs: [{ type: 'uint8' }], stateMutability: 'view', type: 'function' },
  { constant: true, inputs: [], name: 'underlying', outputs: [{ type: 'address' }], stateMutability: 'view', type: 'function' }, // not present on vBNB, will throw
  { name: 'balanceOfUnderlying', inputs: [{ type: 'address', name: 'owner' }], outputs: [{ type: 'uint256' }], stateMutability: 'nonpayable', type: 'function' },
];

const ERC20Abi = [
  { constant: true, inputs: [], name: 'symbol', outputs: [{ type: 'string' }], stateMutability: 'view', type: 'function' },
  { constant: true, inputs: [], name: 'decimals', outputs: [{ type: 'uint8' }], stateMutability: 'view', type: 'function' },
  { constant: true, inputs: [], name: 'name', outputs: [{ type: 'string' }], stateMutability: 'view', type: 'function' },
];

function toNumberString(value) {
  try { return value?.toString?.() ?? '0'; } catch (_e) { return '0'; }
}

const providerCache = new Map();
function getProvider(rpc) {
  const key = `${rpc}|${BNB_CHAIN_ID}`;
  const cached = providerCache.get(key);
  if (cached) return cached;
  const p = new providers.StaticJsonRpcProvider(rpc, BNB_CHAIN_ID);
  providerCache.set(key, p);
  return p;
}

async function tryFetchOnChain(rpc, walletAddress) {
  const provider = getProvider(rpc);
  const comptroller = new Contract(VENUS_COMPTROLLER, ComptrollerAbi, provider);
  const markets = await comptroller.getAllMarkets();
  const assetsIn = await comptroller.getAssetsIn(walletAddress).catch(() => []);
  const assetsInSet = new Set((assetsIn || []).map((a) => String(a).toLowerCase()));
  const oracleAddr = await comptroller.oracle();
  const oracle = new Contract(oracleAddr, PriceOracleAbi, provider);

  const rows = [];
  for (const vAddr of markets) {
    const v = new Contract(vAddr, VTokenAbi, provider);
    let underlyingAddr = null;
    let underlyingSymbol = 'BNB';
    let underlyingDecimals = 18;
    try {
      underlyingAddr = await v.underlying();
      if (underlyingAddr && underlyingAddr !== '0x0000000000000000000000000000000000000000') {
        const u = new Contract(underlyingAddr, ERC20Abi, provider);
        underlyingSymbol = await u.symbol().catch(() => underlyingSymbol);
        underlyingDecimals = await u.decimals().catch(() => 18);
      }
    } catch (_e) {
      // vBNB path; keep defaults
    }

    const [borrowStored, priceMantissa] = await Promise.all([
      v.borrowBalanceStored(walletAddress).catch(() => EBig.from(0)),
      oracle.getUnderlyingPrice(vAddr).catch(() => EBig.from(0)),
    ]);

    // Use callStatic to compute underlying supply amount (handles exchange rate properly)
    const supplyUnderlying = await v.callStatic.balanceOfUnderlying(walletAddress).catch(() => EBig.from(0));

    const bnSupplyRaw = new BigNumber(supplyUnderlying.toString()); // underlying base units
    const bnBorrowRaw = new BigNumber(borrowStored.toString());    // underlying base units
    const bnPrice = new BigNumber(priceMantissa.toString());       // Compound-style: USD price scaled by 1e(36 - underlyingDecimals)

    const pow = (n) => new BigNumber(10).pow(n);
    const denomUSD = pow(36); // USD = raw * price / 1e36
    const supplyUSD = bnSupplyRaw.multipliedBy(bnPrice).dividedBy(denomUSD);
    const borrowUSD = bnBorrowRaw.multipliedBy(bnPrice).dividedBy(denomUSD);

    const bnSupplyTokens = bnSupplyRaw.dividedBy(pow(underlyingDecimals));
    const bnBorrowTokens = bnBorrowRaw.dividedBy(pow(underlyingDecimals));

    const supplyUSDNum = Number(supplyUSD.toString());
    const borrowUSDNum = Number(borrowUSD.toString());

    if (supplyUSDNum <= 0 && borrowUSDNum <= 0 && bnSupplyRaw.isZero() && bnBorrowRaw.isZero()) {
      continue;
    }

    rows.push({
      id: `${underlyingSymbol}-${vAddr}`,
      underlyingAsset: underlyingAddr || vAddr,
      scaledATokenBalance: '0',
      usageAsCollateralEnabledOnUser: assetsInSet.has(String(vAddr).toLowerCase()),
      scaledVariableDebt: '0',
      reserve: {
        originalId: vAddr,
        id: vAddr,
        symbol: underlyingSymbol,
        name: underlyingSymbol,
        decimals: Number(underlyingDecimals || 18),
        underlyingAsset: underlyingAddr || '',
        baseLTVasCollateral: '0',
        liquidityIndex: '0',
        reserveLiquidationThreshold: '0',
        reserveLiquidationBonus: '0',
        variableBorrowIndex: '0',
        variableBorrowRate: '0',
        availableLiquidity: '0',
        liquidityRate: '0',
        totalScaledVariableDebt: '0',
        lastUpdateTimestamp: 0,
        borrowCap: '0',
        supplyCap: '0',
        debtCeiling: '0',
        debtCeilingDecimals: 0,
        isolationModeTotalDebt: '0',
        unbacked: '0',
        virtualAccActive: false,
        virtualUnderlyingBalance: '0',
        priceInMarketReferenceCurrency: '0',
      },
      underlyingBalance: toNumberString(bnSupplyTokens.toFixed()),
      underlyingBalanceUSD: toNumberString(supplyUSD.toFixed()),
      variableBorrows: toNumberString(bnBorrowTokens.toFixed()),
      variableBorrowsUSD: toNumberString(borrowUSD.toFixed()),
      totalBorrows: toNumberString(bnBorrowTokens.toFixed()),
      totalBorrowsUSD: toNumberString(borrowUSD.toFixed()),
    });
  }

  const totalSupplyUSD = rows.reduce((acc, r) => acc + (Number(r.underlyingBalanceUSD || '0') || 0), 0);
  const totalBorrowUSD = rows.reduce((acc, r) => acc + (Number(r.totalBorrowsUSD || '0') || 0), 0);

  // Compute Venus-like risk metrics
  // collateralFactorMantissa is markets(vToken)[1] scaled by 1e18
  let weightedCFNumerator = 0; // sum(supplyUSD * cf)
  let collateralUSDForLTV = 0; // sum(supplyUSD) of assets entered
  for (const vAddr of markets) {
    try {
      const info = await comptroller.markets(vAddr);
      const cfMantissa = new BigNumber(info[1]?.toString?.() || '0');
      // Find row for this vAddr
      const row = rows.find((r) => r.underlyingAsset.toLowerCase() === String(vAddr).toLowerCase() || r.reserve.id === vAddr);
      if (!row) continue;
      const entered = assetsInSet.has(String(vAddr).toLowerCase());
      if (!entered) continue;
      const supplyUSD = Number(row.underlyingBalanceUSD || '0') || 0;
      weightedCFNumerator += supplyUSD * Number(cfMantissa.dividedBy(new BigNumber('1e18')).toString());
      collateralUSDForLTV += supplyUSD;
    } catch (_e) {}
  }

  const adjustedCollateralUSD = weightedCFNumerator; // already applied cf
  const ltvPct = collateralUSDForLTV > 0 ? (totalBorrowUSD / collateralUSDForLTV) * 100 : 0;
  const liqThresholdPct = collateralUSDForLTV > 0 ? (adjustedCollateralUSD / collateralUSDForLTV) * 100 : 0;
  const health = totalBorrowUSD > 0 ? (adjustedCollateralUSD / totalBorrowUSD) : (collateralUSDForLTV > 0 ? 10 : 0);
  const ray = new BigNumber('1e18');
  const healthRay = new BigNumber(health.toString()).multipliedBy(ray);
  const healthFactorStr = healthRay.isFinite() ? healthRay.integerValue(BigNumber.ROUND_DOWN).toFixed() : '0';
  const ltvBps = Math.round(ltvPct * 100);
  const liqBps = Math.round(liqThresholdPct * 100);

  return {
    networkKey: 'bnb',
    networkLabel: 'BNB Chain · Venus',
    chainId: BNB_CHAIN_ID,
    poolSummary: {
      totalCollateralBase: toNumberString(totalSupplyUSD),
      totalDebtBase: toNumberString(totalBorrowUSD),
      availableBorrowsBase: toNumberString(Math.max(adjustedCollateralUSD - totalBorrowUSD, 0)),
      currentLiquidationThreshold: toNumberString(liqBps),
      ltv: toNumberString(ltvBps),
      healthFactor: toNumberString(healthFactorStr),
    },
    userSummary: {
      userReservesData: rows,
    },
  };
}

export async function fetchVenusUserData(walletAddress) {
  const results = [];
  try {
    // Build RPC list with env override (comma-separated supported)
    const envOverride = process.env.REACT_APP_RPC_BNB;
    const rpcListFromEnv = envOverride
      ? envOverride.split(',').map((s) => s.trim()).filter(Boolean)
      : [];
    const rpcCandidates = rpcListFromEnv.length > 0 ? rpcListFromEnv : BNB_RPC_CANDIDATES;

    let lastError = null;
    for (const rpc of rpcCandidates) {
      try {
        const res = await tryFetchOnChain(rpc, walletAddress);
        results.push(res);
        lastError = null;
        break;
      } catch (err) {
        lastError = err;
      }
    }
    if (lastError) {
      results.push({ networkKey: 'bnb', networkLabel: 'BNB Chain · Venus', error: lastError?.message || 'Failed to load data' });
    }
  } catch (error) {
    results.push({ networkKey: 'bnb', networkLabel: 'BNB Chain · Venus', error: error?.message || 'Failed to load data' });
  }
  return results;
}
